import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { User, Mail, Lock, ArrowRight, Sparkles, Target, Users } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Particles } from '@/components/ui/particles';

const Signup: React.FC = () => {
  const location = useLocation();
  const { user, loading, signUp } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaitingEmailConfirmation, setAwaitingEmailConfirmation] = useState(false);

  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { sessionCreated } = await signUp(email, password, name);
      if (!sessionCreated) {
        setAwaitingEmailConfirmation(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your account');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (awaitingEmailConfirmation) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center bg-white rounded-2xl border border-[#DADADA] shadow-[0_24px_80px_-24px_rgba(0,0,0,0.12)] p-10 sm:p-12">
          <div className="w-14 h-14 rounded-2xl bg-[#121212] text-white flex items-center justify-center mx-auto mb-6">
            <Mail className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h1 className="text-2xl font-semibold font-poppins text-[#1A1A1A] tracking-tight">Check your inbox</h1>
          <p className="text-[#555555] mt-3 leading-relaxed">
            We sent a confirmation link to <span className="font-medium text-[#1A1A1A]">{email}</span>. Open it to
            activate your account, then sign in.
          </p>
          <Link
            to="/login"
            state={location.state}
            className="inline-flex items-center justify-center gap-2 mt-8 w-full bg-[#121212] text-white py-3 rounded-xl font-semibold hover:bg-[#2A2A2A] transition-colors"
          >
            Back to sign in
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-[#0C0C0C] via-[#141414] to-[#1C1C1C] text-white px-12 xl:px-16 py-14">
        <div className="absolute inset-0 opacity-[0.35]">
          <Particles className="h-full w-full" quantity={80} color="#737373" size={0.5} staticity={40} ease={70} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C]/90 via-transparent to-transparent pointer-events-none" />

        <div className="relative z-10">
          <Link to="/" className="inline-block text-2xl font-bold font-poppins tracking-tight hover:opacity-90 transition-opacity">
            Kipr
          </Link>
        </div>

        <div className="relative z-10 max-w-md space-y-10">
          <div>
            <h2 className="text-3xl xl:text-[2rem] font-semibold font-poppins leading-tight tracking-tight text-white">
              Clinical training,
              <span className="block text-[#B8B8B8] mt-1">designed for real sessions.</span>
            </h2>
            <p className="mt-5 text-[#B0B0B0] text-[15px] leading-relaxed">
              Join practitioners building sharper skills with scenario-based practice and structured feedback.
            </p>
          </div>

          <ul className="space-y-5">
            {[
              { icon: Sparkles, text: 'Interactive scenarios tailored to ABA and clinical workflows' },
              { icon: Target, text: 'Focused drills that track progress over time' },
              { icon: Users, text: 'Built for individuals and growing teams' },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/[0.08]">
                  <Icon className="w-[18px] h-[18px] text-[#E5E5E5]" strokeWidth={1.75} />
                </span>
                <span className="text-[14px] leading-snug text-[#D4D4D4] pt-1.5">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-[13px] text-[#737373]">© {new Date().getFullYear()} Kipr</p>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 py-12 lg:py-16">
        <div className="lg:hidden mb-10 text-center">
          <Link to="/" className="text-2xl font-bold font-poppins text-[#1A1A1A]">
            Kipr
          </Link>
          <p className="text-sm text-[#555555] mt-2">Create your account</p>
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="mb-8 hidden lg:block">
            <h1 className="text-2xl sm:text-[1.75rem] font-semibold font-poppins text-[#1A1A1A] tracking-tight">
              Create your account
            </h1>
            <p className="text-[#555555] mt-2 text-[15px]">Start training in minutes. No credit card required.</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E8E8] shadow-[0_20px_70px_-28px_rgba(0,0,0,0.14)] p-7 sm:p-9">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="signup-name" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Full name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                    size={18}
                    strokeWidth={1.75}
                  />
                  <input
                    id="signup-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Work email
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                    size={18}
                    strokeWidth={1.75}
                  />
                  <input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                    size={18}
                    strokeWidth={1.75}
                  />
                  <input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                    placeholder="At least 8 characters"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100/80 rounded-xl px-4 py-3 leading-snug">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full flex items-center justify-center gap-2 bg-[#121212] text-white py-3.5 rounded-xl text-[15px] font-semibold hover:bg-[#2A2A2A] disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                {isSubmitting ? (
                  'Creating account…'
                ) : (
                  <>
                    Create account
                    <ArrowRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                      strokeWidth={2}
                    />
                  </>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#555555]">
              Already have an account?{' '}
              <Link
                to="/login"
                state={location.state}
                className="font-semibold text-[#1A1A1A] hover:underline underline-offset-4"
              >
                Sign in
              </Link>
            </p>
          </div>

          <p className="mt-8 text-center text-xs text-[#A3A3A3] leading-relaxed max-w-sm mx-auto">
            By creating an account you agree to our{' '}
            <Link to="/terms" className="underline hover:no-underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:no-underline">
              Privacy Policy
            </Link>
            , and to follow your organization&apos;s policies for clinical training data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
